import { cloneDeep } from "lodash";
import { AppAddressCountries } from "../../../utils/appEnums/AppEnums";
import { defaultPlaceAddress, PlaceAddress } from "../../../utils/appInterfaces/AppInterfaces";
import { BO_CORE_DEFAULT_VALUE } from "../../../utils/AppUtils";
import { AddressLocation } from "../SingleAddress/components/GoogleAutocompleteAddress/GoogleAutocompleteAddressTypes";
import {
    DEGRADED_PLACE_COORDINATE,
    isSavedLocationId,
    newAddressLocation,
} from "../SingleAddress/components/GoogleAutocompleteAddress/GoogleAutocompleteAddressUtils";

/**
 * NG-7511: structured address fields used by the manual-entry fallback overlay when Google Places
 * is unavailable. Mirrors the inline fields on the Profile page (and reuses the same i18n keys).
 *
 * NG-8841: declared here rather than in `ManualAddressFields.tsx` so pure-logic consumers can reach
 * the shape without pulling a React component (and MUI with it) into their module graph.
 */
export interface IManualAddressFormValues {
    addressLine1: string;
    addressLine2: string;
    city: string;
    county: string;
    state: string;
    zipCode: string;
}

export const defaultManualAddressValues: IManualAddressFormValues = {
    addressLine1: "",
    addressLine2: "",
    city: "",
    county: "",
    state: "",
    zipCode: "",
};

/**
 * NG-7511: convert manually-entered address fields into the shapes consumed by the existing
 * autocomplete components. A manual address is always degraded: it was never geocoded, so it
 * carries `isVerified=false` and the shared `DEGRADED_PLACE_COORDINATE` sentinel (matching
 * `defaultAddressLocation`).
 */

/** Trim a stored field and map the backend `"n/a"` sentinel back to an empty string. */
const cleanStoredField = (value: unknown): string => {
    const trimmed = typeof value === "string" ? value.trim() : "";
    return trimmed === BO_CORE_DEFAULT_VALUE ? "" : trimmed;
};

interface IParsedFullAddress {
    addressLine1: string;
    city: string;
    state: string;
    zipCode: string;
}

export const parseFullAddress = (fullAddress: unknown): IParsedFullAddress => {
    const empty: IParsedFullAddress = { addressLine1: "", city: "", state: "", zipCode: "" };
    if (typeof fullAddress !== "string") {
        return empty;
    }
    const parts = fullAddress
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length < 2) {
        return empty;
    }

    let stateZipIndex = -1;
    for (let i = parts.length - 1; i >= 0; i -= 1) {
        if (/\d/.test(parts[i])) {
            stateZipIndex = i;
            break;
        }
    }
    if (stateZipIndex <= 0) {
        return empty;
    }

    const tokens = parts[stateZipIndex].split(/\s+/).filter(Boolean);
    const stateTokens: string[] = [];
    let cursor = 0;
    while (cursor < tokens.length && /^[A-Za-z.]+$/.test(tokens[cursor])) {
        stateTokens.push(tokens[cursor]);
        cursor += 1;
    }

    return {
        addressLine1: parts.slice(0, stateZipIndex - 1).join(", "),
        city: parts[stateZipIndex - 1] || "",
        state: stateTokens.join(" "),
        zipCode: tokens.slice(cursor).join(" "),
    };
};

export const locationToManualAddressValues = (location: unknown): Partial<IManualAddressFormValues> => {
    if (!location || typeof location !== "object") {
        return {};
    }
    const source = location as Record<string, unknown>;

    const addressLine1 = cleanStoredField(source.addressLine1);
    const city = cleanStoredField(source.city ?? source.cityName);
    const state = cleanStoredField(source.state ?? source.stateName);
    const zipCode = cleanStoredField(source.zipCode ?? source.postalCode);

    // Only parse when something is missing — a fully-structured record needs no fullAddress fallback.
    const parsed = !addressLine1 || !city || !state || !zipCode ? parseFullAddress(cleanStoredField(source.fullAddress)) : null;

    return {
        addressLine1: addressLine1 || parsed?.addressLine1 || "",
        addressLine2: cleanStoredField(source.addressLine2),
        city: city || parsed?.city || "",
        county: cleanStoredField(source.county),
        state: state || parsed?.state || "",
        zipCode: zipCode || parsed?.zipCode || "",
    };
};

/** Builds a Google-style "line1 line2, city, state zip" summary for the single-line display field. */
export const composeFullAddress = (values: IManualAddressFormValues): string => {
    const street = [values.addressLine1, values.addressLine2]
        .map((part) => part.trim())
        .filter(Boolean)
        .join(" ");
    const region = [values.state, values.zipCode]
        .map((part) => part.trim())
        .filter(Boolean)
        .join(" ");
    const cityRegion = [values.city.trim(), region].filter(Boolean).join(", ");
    return [street, cityRegion].filter(Boolean).join(", ");
};

/**
 * V2 path: produce a structured AddressLocation for the `locationAddressName` form field.
 *
 * `existingId` is the id of the address being replaced, when there is one. Editing a saved
 * organization re-uses its Location, so that id has to survive a manual re-entry; a brand-new
 * address has none and must go to the backend as `null` (NG-8840 - the -1 sentinel makes the tenant
 * factory look up a Location that does not exist and 400 the request). `isSavedLocationId` maps
 *  "absent" and "-1" onto null so callers can pass whatever the form happens to hold.
 */
export const manualAddressToLocation = (
    values: IManualAddressFormValues,
    locationType: string,
    country: string | null,
    existingId?: number | null,
): AddressLocation => ({
    ...newAddressLocation,
    ...(isSavedLocationId(existingId) ? { id: existingId } : {}),
    addressLine1: values.addressLine1.trim(),
    addressLine2: values.addressLine2.trim(),
    city: values.city.trim(),
    county: values.county.trim(),
    state: values.state.trim(),
    zipCode: values.zipCode.trim(),
    fullAddress: composeFullAddress(values),
    country: country || AppAddressCountries.UNITED_STATES_OF_AMERICA,
    type: locationType,
    isVerified: false,
    lat: DEGRADED_PLACE_COORDINATE,
    lon: DEGRADED_PLACE_COORDINATE,
});

/**
 * V1 path: synthesize a PlaceAddress so existing `setAddressValuesFromPlacesApi` consumers map the
 * manual fields the same way they map a Google result. The whole street goes into `route` (with an
 * empty `street_number`), since downstream consumers trim the composed `addressLine1`.
 */
export const manualAddressToPlaceAddress = (values: IManualAddressFormValues, country: string | null): PlaceAddress => {
    const placeAddress = cloneDeep(defaultPlaceAddress);
    placeAddress.lat = DEGRADED_PLACE_COORDINATE;
    placeAddress.lon = DEGRADED_PLACE_COORDINATE;
    placeAddress.formatted_address = composeFullAddress(values);
    placeAddress.street_number = { long_name: "", short_name: "", types: ["street_number"] };
    placeAddress.route = { long_name: values.addressLine1.trim(), short_name: values.addressLine1.trim(), types: ["route"] };
    placeAddress.locality = { long_name: values.city.trim(), short_name: values.city.trim(), types: ["locality"] };
    placeAddress.administrative_area_level_2 = { long_name: values.county.trim(), short_name: values.county.trim(), types: ["administrative_area_level_2"] };
    placeAddress.administrative_area_level_1 = { long_name: values.state.trim(), short_name: values.state.trim(), types: ["administrative_area_level_1"] };
    placeAddress.postal_code = { long_name: values.zipCode.trim(), short_name: values.zipCode.trim(), types: ["postal_code"] };
    // Leave country.long_name empty so consumers that re-run checkNewGoogleAddressIsVerified keep the
    // address unverified — a manually-entered address was never geocoded. (short_name retains the value.)
    placeAddress.country = { long_name: "", short_name: country || AppAddressCountries.UNITED_STATES_OF_AMERICA, types: ["country"] };
    return placeAddress;
};
